// src/components/dashboard/ImageUploadTab.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Heading,
    Text,
    useToast,
    VStack,
    HStack,
    SimpleGrid,
    Progress,
    IconButton,
    Image,
    RadioGroup,
    Radio,
    Stack,
    Badge,
    Spinner,
    Center,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Flex,
    Tooltip,
} from '@chakra-ui/react';
import {
    FiUpload,
    FiCheck,
    FiTrash2,
    FiCreditCard,
} from 'react-icons/fi';
import { InfoIcon } from '@chakra-ui/icons';
import { supabase } from '../../config/supabaseClient';

// Mensagens aleatórias para mostrar durante o processamento
const processingMessages = [
    "Analisando linhas e formas...",
    "Criando traçados precisos...",
    "Ajustando os contornos...",
    "Refinando os detalhes artísticos...",
    "Otimizando as linhas para colorir...",
    "Preparando a arte final...",
    "Aplicando estilo Bobbie Goods...",
    "Quase lá! Finalizando os detalhes...",
    "Transformando em desenho para colorir...",
    "Convertendo para linhas perfeitas...",
    "Deixando tudo pronto para você colorir..."
];

const MAX_FILES = 5;
// Prazo de validade da URL assinada em segundos (365 dias = 31536000 segundos)
const URL_EXPIRY_SECONDS = 31536000;

const ImageUploadTab = ({ profile, user, onCheckoutOpen, onProfileUpdate, onImagesUpdate, onSuccess }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [orientation, setOrientation] = useState('horizontal');
    const [processingMessage, setProcessingMessage] = useState('');
    const [processingIndex, setProcessingIndex] = useState(0);
    const [processedCount, setProcessedCount] = useState(0);
    const [totalToProcess, setTotalToProcess] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const cancelRef = React.useRef();

    const toast = useToast();

    // Efeito para alternar as mensagens de processamento
    useEffect(() => {
        let intervalId;

        if (uploading) {
            intervalId = setInterval(() => {
                setProcessingIndex(prevIndex => (prevIndex + 1) % processingMessages.length);
            }, 3000);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [uploading]);

    // Atualizar a mensagem quando o índice muda
    useEffect(() => {
        setProcessingMessage(processingMessages[processingIndex]);
    }, [processingIndex]);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);

        // Verificar limite máximo de 5 arquivos
        if (files.length > MAX_FILES) {
            toast({
                title: 'Limite de arquivos excedido',
                description: `Você pode selecionar no máximo ${MAX_FILES} imagens por vez.`,
                status: 'warning',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        // Verificar se o total de arquivos excederá o limite
        if (selectedFiles.length + files.length > MAX_FILES) {
            toast({
                title: 'Limite de arquivos excedido',
                description: `Você já selecionou ${selectedFiles.length} arquivos. O máximo é ${MAX_FILES}.`,
                status: 'warning',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        // Verificar se o usuário selecionou mais arquivos do que créditos disponíveis
        if (files.length > profile?.creditos) {
            toast({
                title: 'Créditos insuficientes',
                description: `Você selecionou ${files.length} imagens, mas possui apenas ${profile?.creditos} créditos.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        // Verificar tamanho e formato dos arquivos
        const validFiles = files.filter(file => {
            // Verificar tamanho (10MB max)
            if (file.size > 10 * 1024 * 1024) {
                toast({
                    title: 'Arquivo muito grande',
                    description: `${file.name} excede o tamanho máximo de 10MB.`,
                    status: 'warning',
                    duration: 5000,
                    isClosable: true,
                });
                return false;
            }

            // Verificar formato
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
            if (!validTypes.includes(file.type)) {
                toast({
                    title: 'Formato inválido',
                    description: `${file.name} não é um formato aceito. Use JPG ou PNG.`,
                    status: 'warning',
                    duration: 5000,
                    isClosable: true,
                });
                return false;
            }

            return true;
        });

        // Adicionar previews dos arquivos
        const newFiles = validFiles.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            name: file.name,
            size: file.size,
        }));

        setSelectedFiles(prev => [...prev, ...newFiles]);
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => {
            const newFiles = [...prev];
            // Liberar URL do preview para evitar memory leak
            URL.revokeObjectURL(newFiles[index].preview);
            newFiles.splice(index, 1);
            return newFiles;
        });
    };

    // Função para excluir uma imagem (tanto original quanto gerada)
    const deleteImage = async (imageId, originalUrl, generatedUrl) => {
        try {
            setIsDeleting(true);

            // 1. Extrair os caminhos das URLs para excluir do storage
            // Extrair o caminho do arquivo original
            const originalPath = extractPathFromUrl(originalUrl);
            const generatedPath = extractPathFromUrl(generatedUrl);

            // 2. Excluir a imagem original do storage
            if (originalPath) {
                const { error: originalDeleteError } = await supabase.storage
                    .from('original-images')
                    .remove([originalPath]);

                if (originalDeleteError) {
                    console.error('Erro ao excluir imagem original:', originalDeleteError);
                }
            }

            // 3. Excluir a imagem gerada do storage
            if (generatedPath) {
                const { error: generatedDeleteError } = await supabase.storage
                    .from('generated-images')
                    .remove([generatedPath]);

                if (generatedDeleteError) {
                    console.error('Erro ao excluir imagem gerada:', generatedDeleteError);
                }
            }

            // 4. Excluir o registro do banco de dados
            const { error: dbDeleteError } = await supabase
                .from('images')
                .delete()
                .eq('id', imageId);

            if (dbDeleteError) {
                throw dbDeleteError;
            }

            // 5. Notificar sucesso
            toast({
                title: 'Imagem excluída',
                description: 'A imagem foi excluída com sucesso.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // 6. Atualizar a galeria
            if (onImagesUpdate) {
                onImagesUpdate();
            }

        } catch (error) {
            console.error('Erro ao excluir imagem:', error);

            toast({
                title: 'Erro na exclusão',
                description: error.message || 'Ocorreu um erro ao excluir a imagem.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsDeleting(false);
            setImageToDelete(null);
            setIsAlertOpen(false);
        }
    };

    // Função auxiliar para extrair o caminho do arquivo da URL do Supabase
    const extractPathFromUrl = (url) => {
        if (!url) return null;

        try {
            // Tentar extrair o caminho da URL
            const matches = url.match(/\/storage\/v1\/object\/public\/[^/]+\/([^?]+)/);
            if (matches && matches[1]) {
                return decodeURIComponent(matches[1]);
            }

            // Tentar extrair o caminho da URL assinada
            const signedMatches = url.match(/\/storage\/v1\/object\/sign\/[^/]+\/([^?]+)/);
            if (signedMatches && signedMatches[1]) {
                return decodeURIComponent(signedMatches[1]);
            }

            return null;
        } catch (error) {
            console.error('Erro ao extrair caminho da URL:', error);
            return null;
        }
    };

    // Função para confirmar exclusão
    const confirmDelete = (image) => {
        setImageToDelete(image);
        setIsAlertOpen(true);
    };

    // Função auxiliar para ler arquivo como base64
    const readFileAsBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    };

    // Função auxiliar para converter base64 para Blob
    const base64ToBlob = (base64, type = 'image/png') => {
        const byteCharacters = atob(base64);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);

            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type });
    };

    // Função para converter imagem em desenho para colorir
    const convertImageToDrawing = async (fileData) => {
        const { file } = fileData;

        try {
            // Ler a imagem como base64
            const base64 = await readFileAsBase64(file);

            // Converter base64 data URL para string base64 pura
            const base64Image = base64.split(',')[1];

            // Prompt para criar o desenho para colorir
            const prompt = `
            ############################################################
            #  COLORING-PAGE SAAS - v2.0 (BOBBIE GOODS STYLE)          #
            ############################################################
            <ROLE>
            Você é um ilustrador digital especialista em transformar QUALQUER foto
            em páginas para colorir "cute & comfy", capturando o hype do Bobbie Goods
            para crianças e adultos.
            </ROLE>

            <STYLE_GUIDE>
            • Linha única, preta, contínua, espessura equivalente a 4 px @300 dpi  
            • Nenhum preenchimento, sombra ou textura - áreas vazias para colorir  
            • Estética "kawaii realista": proporções levemente cabeçudas, olhos vivos  
            • No máx. 5 elementos secundários simples (flores, corações, estrelas)  
            • Amplo espaço em branco; fácil de imprimir e pintar com lápis/canetinha  
            </STYLE_GUIDE>

            <OUTPUT_SPEC>
            • Formato: ${orientation === 'horizontal' ? 'HORIZONTAL 3:2 - 1536x1024' : 'VERTICAL 2:3 - 1024x1536'} px @300 dpi
            </OUTPUT_SPEC>

            <REFERENCE_IMAGE>
            Use a imagem anexa como base. Se vários sujeitos,
            mantenha APENAS o(s) elemento(s) mais proeminente(s).  
            </REFERENCE_IMAGE>

            <CONVERSION_STEPS>
            1. Reconstruir contornos essenciais segundo STYLE_GUIDE.  
            2. Ajustar enquadramento para o OUTPUT_SPEC (${orientation === 'horizontal' ? '3:2' : '2:3'}),
            expandindo ou croppando sem remover elementos do fundo.
            3. Inserir de 3 a 5 elementos decorativos tematicamente coerentes
            (opcional se já houver cenário claro).  
            4. Garantir linhas totalmente fechadas para prevenir vazamento de tinta.  
            </CONVERSION_STEPS>

            <BACKGROUND_GUIDE>
            • Analise a foto e identifique até 5 elementos de fundo que definem o ambiente
            (móveis, letreiros, luminárias, janelas, paisagens, etc.).
            • Recrie esses elementos em linhas pretas contínuas, mantendo posição
            e proporção aproximadas, mas sem texturas ou sombras.
            • Se o fundo for muito ocupado, escolha apenas os itens mais
            característicos e descarte o restante para preservar espaço em branco.
            • O foco visual deve continuar no(s) sujeito(s) principal(is);
            o fundo serve apenas como ambientação suave.
            • Nunca adicione preenchimentos, gradientes ou padrões complexos.
            </BACKGROUND_GUIDE>

            <MODERATION_POLICY>
            Se a imagem contiver nudez, violência explícita ou for ilegível,
            não gere. Responda apenas:
            "Não consegui processar essa imagem. Envie outra, por favor 😊. Violou os direitos"
            </MODERATION_POLICY>
            `;

            // Obter token de autenticação
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData?.session?.access_token;

            if (!token) {
                throw new Error('Não foi possível obter o token de autenticação');
            }

            // Chamar a função edge
            const response = await fetch('https://mlliabdepyirxtaytcco.functions.supabase.co/process-image', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    image: base64Image,
                    prompt: prompt,
                    orientation: orientation,
                    userId: user.id,
                    fileName: file.name
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`Edge Function Error: ${JSON.stringify(errorData)}`);
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Erro desconhecido ao processar imagem');
            }

            // Converter o base64 para um Blob
            const blob = base64ToBlob(data.image, 'image/png');

            // Criar um arquivo a partir do Blob
            const generatedFile = new File(
                [blob],
                `drawing_${file.name.replace(/\.[^/.]+$/, "")}.png`,
                { type: 'image/png' }
            );

            return {
                generatedFile,
                previewUrl: URL.createObjectURL(blob)
            };
        } catch (error) {
            console.error('Erro ao processar imagem:', error);
            throw error;
        }
    };

    // Processar uma imagem individual e atualizar o progresso
    const processImageAndUpdateProgress = async (fileData, index) => {
        const { file } = fileData;
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}_${Date.now()}_${index}.${fileExt}`;

        try {
            // 1. Upload da imagem original para o Supabase Storage
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('original-images')
                .upload(`${user.id}/${fileName}`, file);

            if (uploadError) throw uploadError;

            // 2. Obter URL assinada da imagem original com prazo de 1 ano
            const { data: urlData, error: urlError } = await supabase.storage
                .from('original-images')
                .createSignedUrl(`${user.id}/${fileName}`, URL_EXPIRY_SECONDS); // 1 ano

            if (urlError) throw urlError;

            const originalImageUrl = urlData.signedUrl;

            // 3. Processar a imagem com a Edge Function
            const { generatedFile, previewUrl } = await convertImageToDrawing(fileData);

            // 4. Upload da imagem gerada para o Supabase
            const generatedFileName = `generated_${fileName.replace(/\.[^/.]+$/, "")}.png`;

            const { data: generatedUploadData, error: generatedUploadError } = await supabase.storage
                .from('generated-images')
                .upload(`${user.id}/${generatedFileName}`, generatedFile);

            if (generatedUploadError) throw generatedUploadError;

            // 5. Obter URL assinada da imagem gerada com prazo de 1 ano
            const { data: generatedUrlData, error: generatedUrlError } = await supabase.storage
                .from('generated-images')
                .createSignedUrl(`${user.id}/${generatedFileName}`, URL_EXPIRY_SECONDS); // 1 ano

            if (generatedUrlError) throw generatedUrlError;

            const generatedImageUrl = generatedUrlData.signedUrl;

            // 6. Salvar os dados no banco de dados
            const { data: imageData, error: imageError } = await supabase
                .from('images')
                .insert([
                    {
                        user_id: user.id,
                        original_image_url: originalImageUrl,
                        generated_image_url: generatedImageUrl,
                        estilo: orientation, // Usando orientação como estilo
                        title: file.name.split('.')[0], // Usar o nome do arquivo como título
                    }
                ])
                .select();

            if (imageError) throw imageError;

            // 7. Atualizar créditos do usuário
            const { error: creditError } = await supabase.rpc('decrement_credits', {
                user_id: user.id,
                amount: 1
            });

            if (creditError) throw creditError;

            // Retornar dados da imagem processada
            return {
                id: imageData[0].id,
                originalUrl: originalImageUrl,
                generatedUrl: generatedImageUrl,
                title: imageData[0].title,
            };
        } catch (error) {
            console.error(`Erro ao processar o arquivo ${file.name}:`, error);
            throw error;
        }
    };

    // Processar imagens de forma assíncrona
    const processImages = async () => {
        if (selectedFiles.length === 0) return;

        if (selectedFiles.length > profile?.creditos) {
            toast({
                title: 'Créditos insuficientes',
                description: `Você precisa de ${selectedFiles.length} créditos para processar estas imagens.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        setUploading(true);
        setProgress(0);
        setProcessedCount(0);
        setTotalToProcess(selectedFiles.length);

        const results = [];
        const promises = [];

        try {
            // Iniciar processamento de todas as imagens simultaneamente
            selectedFiles.forEach((fileData, index) => {
                const promise = processImageAndUpdateProgress(fileData, index)
                    .then(result => {
                        results.push(result);
                        setProcessedCount(prev => prev + 1);
                        setProgress((results.length / selectedFiles.length) * 100);
                    });

                promises.push(promise);
            });

            // Aguardar todas as imagens serem processadas
            await Promise.all(promises);

            // Todas as imagens foram processadas com sucesso
            toast({
                title: 'Processamento concluído',
                description: `${selectedFiles.length} ${selectedFiles.length === 1 ? 'imagem foi transformada' : 'imagens foram transformadas'} com sucesso.`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });

            // Atualizar perfil e imagens
            if (onProfileUpdate) onProfileUpdate();
            if (onImagesUpdate) onImagesUpdate();

            // Limpar arquivos selecionados
            setSelectedFiles([]);

            // Redirecionar para galeria
            if (onSuccess) onSuccess();

        } catch (error) {
            console.error('Erro ao processar imagens:', error);

            toast({
                title: 'Erro no processamento',
                description: error.message || 'Ocorreu um erro ao processar suas imagens.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setUploading(false);
        }
    };

    return (
        // 1. Modificar a estrutura principal do componente para ser mais responsiva:
        <Box>
            <Heading
                size={{ base: "md", md: "lg" }}
                color="gray.700"
                mb={{ base: 4, md: 6 }}
            >
                Transforme suas fotos em desenhos para colorir
            </Heading>

            <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="md">
                <VStack spacing={{ base: 6, md: 8 }} align="stretch">
                    <Text color="gray.700" fontSize={{ base: "md", md: "lg" }}>
                        Selecione até {MAX_FILES} fotos para transformar em desenhos para colorir.
                        Cada imagem utilizará 1 crédito.
                    </Text>

                    {/* Seção de orientação/formato do desenho - com ilustração visual */}
                    {profile?.creditos > 0 ? (
                        <Box>
                            <Box mb={{ base: 5, md: 6 }} px={{ base: 1, md: 0 }}>
                                <Flex
                                    align="center"
                                    mb={3}
                                    justify={{ base: "center", md: "flex-start" }}
                                >
                                    <Text
                                        color="gray.700"
                                        fontSize={{ base: "md", md: "lg" }}
                                        fontWeight="bold"
                                    >
                                        Formato do desenho:
                                    </Text>
                                    <Tooltip label="Escolha o formato em que seu desenho será gerado" hasArrow placement="top">
                                        <Box as="span" ml={1} color="blue.500" cursor="help">
                                            <InfoIcon boxSize={4} />
                                        </Box>
                                    </Tooltip>
                                </Flex>

                                <RadioGroup onChange={setOrientation} value={orientation} colorScheme="blue">
                                    <Stack
                                        direction={{ base: "column", sm: "row" }}
                                        spacing={{ base: 3, md: 5 }}
                                        width="100%"
                                    >
                                        <Radio value="horizontal" width={{ base: "100%", sm: "auto" }}>
                                            <Flex
                                                p={4}
                                                bg={orientation === "horizontal" ? "blue.50" : "gray.50"}
                                                borderRadius="md"
                                                borderWidth={2}
                                                borderColor={orientation === "horizontal" ? "blue.500" : "gray.100"}
                                                transition="all 0.3s"
                                                _hover={{ bg: orientation === "horizontal" ? "blue.50" : "blue.50", borderColor: "blue.300" }}
                                                width={{ base: "100%", sm: "auto" }}
                                                justify="space-between"
                                                align="center"
                                                direction={{ base: "column", md: "row" }}
                                            >
                                                <Flex align="center" mb={{ base: 2, md: 0 }}>
                                                    {/* Ícone ilustrativo para formato horizontal */}
                                                    <Box
                                                        bg={orientation === "horizontal" ? "blue.100" : "gray.100"}
                                                        borderRadius="md"
                                                        p={2}
                                                        mr={3}
                                                        width="60px"
                                                        height="40px"
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        border="1px dashed"
                                                        borderColor={orientation === "horizontal" ? "blue.300" : "gray.300"}
                                                    >
                                                        <Box
                                                            as="svg"
                                                            viewBox="0 0 24 16"
                                                            width="100%"
                                                            height="100%"
                                                            fill={orientation === "horizontal" ? "blue.500" : "gray.500"}
                                                        >
                                                            <rect width="24" height="16" rx="2" fillOpacity="0.3" />
                                                            <path d="M2 2H22V14H2z" fillOpacity="0" stroke="currentColor" strokeWidth="1" />
                                                        </Box>
                                                    </Box>

                                                    <Box>
                                                        <Text
                                                            fontWeight={orientation === "horizontal" ? "bold" : "medium"}
                                                            fontSize={{ base: "md", md: "md" }}
                                                            mb={0}
                                                        >
                                                            Horizontal
                                                        </Text>
                                                        <Text
                                                            fontSize="xs"
                                                            color="gray.600"
                                                            display={{ base: "none", md: "block" }}
                                                        >
                                                            Formato paisagem
                                                        </Text>
                                                    </Box>
                                                </Flex>

                                                <Badge
                                                    colorScheme="blue"
                                                    variant={orientation === "horizontal" ? "solid" : "outline"}
                                                    px={2}
                                                    fontSize={{ base: "xs", md: "sm" }}
                                                >
                                                    1536×1024
                                                </Badge>
                                            </Flex>
                                        </Radio>

                                        <Radio value="vertical" width={{ base: "100%", sm: "auto" }}>
                                            <Flex
                                                p={4}
                                                bg={orientation === "vertical" ? "purple.50" : "gray.50"}
                                                borderRadius="md"
                                                borderWidth={2}
                                                borderColor={orientation === "vertical" ? "purple.500" : "gray.100"}
                                                transition="all 0.3s"
                                                _hover={{ bg: orientation === "vertical" ? "purple.50" : "purple.50", borderColor: "purple.300" }}
                                                width={{ base: "100%", sm: "auto" }}
                                                justify="space-between"
                                                align="center"
                                                direction={{ base: "column", md: "row" }}
                                            >
                                                <Flex align="center" mb={{ base: 2, md: 0 }}>
                                                    {/* Ícone ilustrativo para formato vertical */}
                                                    <Box
                                                        bg={orientation === "vertical" ? "purple.100" : "gray.100"}
                                                        borderRadius="md"
                                                        p={2}
                                                        mr={3}
                                                        width="40px"
                                                        height="60px"
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        border="1px dashed"
                                                        borderColor={orientation === "vertical" ? "purple.300" : "gray.300"}
                                                    >
                                                        <Box
                                                            as="svg"
                                                            viewBox="0 0 16 24"
                                                            width="100%"
                                                            height="100%"
                                                            fill={orientation === "vertical" ? "purple.500" : "gray.500"}
                                                        >
                                                            <rect width="16" height="24" rx="2" fillOpacity="0.3" />
                                                            <path d="M2 2H14V22H2z" fillOpacity="0" stroke="currentColor" strokeWidth="1" />
                                                        </Box>
                                                    </Box>

                                                    <Box>
                                                        <Text
                                                            fontWeight={orientation === "vertical" ? "bold" : "medium"}
                                                            fontSize={{ base: "md", md: "md" }}
                                                            mb={0}
                                                        >
                                                            Vertical
                                                        </Text>
                                                        <Text
                                                            fontSize="xs"
                                                            color="gray.600"
                                                            display={{ base: "none", md: "block" }}
                                                        >
                                                            Formato retrato
                                                        </Text>
                                                    </Box>
                                                </Flex>

                                                <Badge
                                                    colorScheme="purple"
                                                    variant={orientation === "vertical" ? "solid" : "outline"}
                                                    px={2}
                                                    fontSize={{ base: "xs", md: "sm" }}
                                                >
                                                    1024×1536
                                                </Badge>
                                            </Flex>
                                        </Radio>
                                    </Stack>
                                </RadioGroup>

                                {/* Texto explicativo */}
                                <Text fontSize="xs" color="gray.500" mt={2} textAlign={{ base: "center", md: "left" }}>
                                    Escolha o formato que melhor se adapta à sua imagem para obter melhores resultados. As imagens de colorir serão geradas neste formato.
                                </Text>
                            </Box>
                            {/* Área de upload/área dos arquivos selecionados */}
                            <Box
                                border="2px dashed"
                                borderColor={selectedFiles.length > 0 ? 'brand.primary' : 'gray.300'}
                                borderRadius="lg"
                                p={{ base: 4, md: 8 }}
                                textAlign="center"
                                transition="all 0.3s"
                                _hover={{ borderColor: 'brand.primary' }}
                            >
                                <input
                                    type="file"
                                    id="fileInput"
                                    multiple
                                    accept="image/jpeg,image/png,image/jpg"
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                    disabled={uploading}
                                />

                                {selectedFiles.length === 0 ? (
                                    <label htmlFor="fileInput" style={{ cursor: 'pointer' }}>
                                        <VStack spacing={4}>
                                            <FiUpload size={40} color="#A5B4E8" />
                                            <Heading size="md" color="gray.700">
                                                Arraste suas fotos ou clique para selecionar
                                            </Heading>
                                            <Text color="gray.500">
                                                Formatos aceitos: JPG, PNG (máx. 10MB por arquivo)
                                            </Text>
                                            <Text color="gray.500" fontWeight="medium">
                                                Máximo de {MAX_FILES} imagens por vez
                                            </Text>
                                        </VStack>
                                    </label>
                                ) : (
                                    <VStack spacing={4}>
                                        <Heading size="sm" color="gray.700">
                                            {selectedFiles.length} {selectedFiles.length === 1 ? 'arquivo selecionado' : 'arquivos selecionados'}
                                        </Heading>

                                        {uploading && (
                                            <Box w="100%">
                                                <HStack justify="space-between" mb={2}>
                                                    <HStack>
                                                        <Spinner size="sm" color="blue.500" mr={2} />
                                                        <Text color="gray.600">
                                                            {processingMessage}
                                                        </Text>
                                                    </HStack>
                                                    <Text color="gray.600" fontWeight="bold">
                                                        {processedCount}/{totalToProcess}
                                                    </Text>
                                                </HStack>
                                                <Progress
                                                    value={progress}
                                                    colorScheme="blue"
                                                    size="sm"
                                                    borderRadius="full"
                                                    hasStripe
                                                    isAnimated
                                                    mb={3}
                                                />
                                                <Box
                                                    p={3}
                                                    borderRadius="md"
                                                    bg="blue.50"
                                                    borderWidth="1px"
                                                    borderColor="blue.200"
                                                >
                                                    <Center>
                                                        <Spinner size="md" color="blue.500" mr={3} />
                                                        <Text fontSize="sm" color="blue.700">
                                                            Cada imagem pode levar até 1 minuto para processar. Por favor, aguarde...
                                                        </Text>
                                                    </Center>
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Preview de imagens selecionadas */}
                                        {!uploading && selectedFiles.length > 0 && (
                                            <SimpleGrid
                                                columns={{ base: 1, sm: 2, md: 3 }}
                                                spacing={4}
                                                w="100%"
                                                mt={4}
                                            >
                                                {selectedFiles.map((fileData, index) => (
                                                    <Box
                                                        key={index}
                                                        borderRadius="md"
                                                        overflow="hidden"
                                                        boxShadow="sm"
                                                        bg="white"
                                                        position="relative"
                                                    >
                                                        <Box
                                                            height="150px"
                                                            width="100%"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                            bg="gray.50"
                                                            overflow="hidden"
                                                            position="relative"
                                                        >
                                                            <Image
                                                                src={fileData.preview}
                                                                alt={fileData.name}
                                                                objectFit="contain"
                                                                maxH="150px"
                                                                maxW="100%"
                                                            />
                                                        </Box>
                                                        <HStack p={2} justify="space-between" bg="gray.50">
                                                            <Text fontSize="sm" noOfLines={1} flex="1">
                                                                {fileData.name}
                                                            </Text>
                                                            <IconButton
                                                                icon={<FiTrash2 />}
                                                                size="sm"
                                                                variant="ghost"
                                                                colorScheme="red"
                                                                onClick={() => removeFile(index)}
                                                                aria-label="Remover arquivo"
                                                            />
                                                        </HStack>
                                                    </Box>
                                                ))}
                                            </SimpleGrid>

                                        )}

                                        {!uploading && (
                                            <Stack
                                                direction={{ base: "column", sm: "row" }}
                                                spacing={{ base: 3, sm: 4 }}
                                                w={{ base: "100%", sm: "auto" }}
                                                mt={4}
                                            >
                                                <Button
                                                    leftIcon={<FiTrash2 />}
                                                    variant="outline"
                                                    onClick={() => setSelectedFiles([])}
                                                    size={{ base: "md", sm: "md" }}
                                                    w={{ base: "100%", sm: "auto" }}
                                                >
                                                    Limpar
                                                </Button>
                                                <Button
                                                    leftIcon={<FiCheck />}
                                                    colorScheme="blue"
                                                    bgGradient="linear(to-r, brand.primary, brand.secondary)"
                                                    color="white"
                                                    onClick={processImages}
                                                    size={{ base: "md", sm: "md" }}
                                                    w={{ base: "100%", sm: "auto" }}
                                                    _hover={{
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: 'md',
                                                    }}
                                                >
                                                    Transformar {selectedFiles.length} {selectedFiles.length === 1 ? 'imagem' : 'imagens'}
                                                </Button>
                                            </Stack>
                                        )}
                                    </VStack>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <VStack spacing={4} p={6} bg="gray.50" borderRadius="lg">
                            <Text fontSize="lg" color="gray.700">
                                Você não possui créditos suficientes para criar desenhos.
                            </Text>
                            <Button
                                colorScheme="blue"
                                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                                color="white"
                                leftIcon={<FiCreditCard />}
                                size="lg"
                                onClick={onCheckoutOpen}
                                _hover={{
                                    transform: 'translateY(-2px)',
                                    boxShadow: 'md',
                                }}
                            >
                                Comprar créditos
                            </Button>
                        </VStack>
                    )}

                    <HStack
                        justify="space-between"
                        flexDir={{ base: "column", sm: "row" }}
                        spacing={{ base: 2, sm: 0 }}
                        align={{ base: "flex-start", sm: "center" }}
                    >
                        <Text
                            color="gray.600"
                            fontSize={{ base: "sm", md: "md" }}
                            mb={{ base: 1, sm: 0 }}
                        >
                            Seus créditos disponíveis: <b>{profile?.creditos || 0}</b>
                        </Text>
                        <Text
                            color="gray.600"
                            fontSize="sm"
                        >
                            Orientação: <b>{orientation === 'horizontal' ? 'Horizontal (1536x1024)' : 'Vertical (1024x1536)'}</b>
                        </Text>
                    </HStack>
                </VStack>
            </Box>

            {/* Diálogo de confirmação para exclusão de imagem */}
            <AlertDialog
                isOpen={isAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => setIsAlertOpen(false)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Excluir Imagem
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Tem certeza que deseja excluir esta imagem? Esta ação não pode ser desfeita.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setIsAlertOpen(false)}>
                                Cancelar
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={() => {
                                    if (imageToDelete) {
                                        deleteImage(
                                            imageToDelete.id,
                                            imageToDelete.originalUrl,
                                            imageToDelete.generatedUrl
                                        );
                                    }
                                }}
                                ml={3}
                                isLoading={isDeleting}
                                loadingText="Excluindo..."
                            >
                                Excluir
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default ImageUploadTab;